import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect } from 'react';
import Loader from '../icons/Loader';
import Input from '../Input/Input';
import DashboardMap from '../DashboardMap/DashboardMap';
import { parseAddressString, formatAddressComponents, validateAddressComponents, parseGoogleAddress } from 'lib';
import eventMarker from "../../assets/map/event-marker.png";
import axiosInstance from "../../api/axiosInstance";
import { backendUrl } from "../../api/constants";
import Logo from "../../assets/imgs/inyc.svg";
import CheckBox from '../CheckBox/CheckBox';
import VenueCard from '../VenueCard/VenueCard';
import { Popup } from 'react-leaflet';
import { MdClear } from "react-icons/md";
import { FcGoogle } from "react-icons/fc";
import { MarkerPopupButton } from '../Map/MapMarker';

import * as libExports from 'lib';
console.log('Available lib exports:', Object.keys(libExports));

/**
 * AddressHandler Component
 * 
 * A form component that handles address input and map location selection.
 * 
 * @component
 * @param {Object} props
 * @param {Object} props.value - Parent controlled value containing address string and location coordinates
 * @param {Function} props.onChange - Callback when address/location changes
 * @param {Array} props.mapMarkers - Additional markers to display on map
 * @param {Object} props.initialValue - Initial address/location value for uncontrolled mode
 * @param {React.Ref} ref - Forwarded ref that exposes component methods
 * @param {Function} props.renderSuggestion - Custom suggestion rendering function
 * @param {Function} props.onSuggestionSelected - Parent-specific selection handling callback
 * @param {Function} props.suggestionFilter - Optional filter function for suggestions
 * @param {boolean} props.recommendCheckbox - Optional prop to conditionally render recommend checkbox
* @param {boolean} props.showVenueCard - Optional prop to conditionally render the venue card if a verified venue is selected
 */
const AddressHandler = forwardRef((props, ref) => {

    const {
        value,
        onChange,
        userLocation,
        showVenueCard,
        mapMarkers = [],
        initialValue = { address: '', location: null },
        renderSuggestion,
        onSuggestionSelected,
        suggestionFilter,
        recommendCheckbox = false,
        hideLabel = false,
        labelRowClassName = "",
    } = props
    // State
    const [locationSuggestions, setLocationSuggestions] = useState([]);
    const [locationLoading, setLocationLoading] = useState(false);
    const [addressEmpty, setAddressEmpty] = useState(true);
    const [validationErrors, setValidationErrors] = useState({
        venueName: '',
        streetAddress: '',
        city: '',
        state: '',
        zipCode: ''
    });
    const [touched, setTouched] = useState({
        venueName: false,
        streetAddress: false,
        city: false,
        state: false,
        zipCode: false
    });
    const [hasInteracted, setHasInteracted] = useState(false);
    
    // Refs
    const isInternalUpdate = useRef(false);
    const mapRef = useRef();
    const locationSearchTimer = useRef();
    const recommendRef = useRef();

    // Hooks
    const isControlled = value !== undefined;

    // Initialize internal state
    const [internalState, setInternalState] = useState(() => ({
        address: parseAddressString(initialValue?.address || ''),
        location: initialValue?.location || null,
        venue: initialValue?.venue || null
    }));

    // Effects
    useEffect(() => {
        if (isControlled && !isInternalUpdate.current) {
            setInternalState({
                address: value?.addressFields || parseAddressString(value?.address || ''),
                location: value?.location || null,
                venue: value?.venue || null
            });
        }
        isInternalUpdate.current = false;
    }, [isControlled, value?.address, value?.addressFields, value?.location, value?.venue]);


    useEffect(() => {
        if (!mapRef.current) return;
        if (userLocation?.lng && userLocation?.lat) {
            mapRef.current.panTo(userLocation);
        }

    }, [userLocation])

    // Fit map to show all venue markers if no location is selected
    useEffect(() => {
        if (!mapRef.current) return;
        // Only fit bounds if no location is currently selected and we have markers
        if (!internalState.location?.coordinates && mapMarkers?.length > 0) {
            // Small delay to ensure map is ready
            setTimeout(() => {
                if (!mapRef.current) return;
                
                // Create bounds that include all markers
                const bounds = mapMarkers
                    .filter(m => m?.lat && m?.lng)
                    .map(m => [m.lat, m.lng]);
                
                // Also include user location if available
                if (userLocation?.lat && userLocation?.lng) {
                    bounds.push([userLocation.lat, userLocation.lng]);
                }
                
                if (bounds.length > 0) {
                    try {
                        // Use fitBounds to show all markers with some padding
                        mapRef.current.fitBounds(bounds, {
                            padding: [30, 30],
                            maxZoom: 14
                        });
                    } catch (e) {
                        // Fallback to centering on first marker if fitBounds fails
                        const firstMarker = mapMarkers[0];
                        if (firstMarker?.lat && firstMarker?.lng) {
                            mapRef.current.panTo({
                                lat: firstMarker.lat,
                                lng: firstMarker.lng
                            });
                        }
                    }
                }
            }, 100);
        }
    }, [mapMarkers, internalState.location?.coordinates, userLocation])

    useEffect(() => {
        if (!mapRef.current) return;

        if (internalState.location?.coordinates) {
            mapRef.current.panTo({
                lat: internalState.location.coordinates[1],
                lng: internalState.location.coordinates[0]
            });
        }
    }, [internalState.location?.coordinates]);

    useEffect(() => {
        const isEmpty = !Object.values(internalState.address).some(val => val?.trim() !== '') && !internalState.location;
        setAddressEmpty(isEmpty);

        // Validate address components whenever they change
        const { errors } = validateAddressComponents(internalState.address);
        setValidationErrors(errors);
    }, [internalState.address]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
        getState: () => ({
            address: formatAddressComponents(internalState.address),
            location: internalState.location,
            venue: internalState.venue
        }),
        getVenueId: () => internalState.venue?._id,
        goToTab: () => {}, // No-op: tabs removed, now single integrated view
        clearAddress,
        setState: (newState) => {
            //if newState is a function , use it with current state 
            if (typeof newState === 'function') {
                let newVal = newState(internalState);
                const { address, location, type, name, venue } = newVal;
                // If address is already an object (parsed components), use it directly
                // Otherwise, parse it from a string
                let parsedAddress;
                if (address && typeof address === 'object' && !Array.isArray(address)) {
                    // Already parsed address components
                    parsedAddress = address;
                } else {
                    // Parse from string (or null/undefined)
                    parsedAddress = parseAddressString(address || '', type === "venue" ? name : null);
                }
                setInternalState({
                    address: parsedAddress,
                    location: location,
                    venue: venue,
                });
            } else {
                const { address, location, type, name, venue } = newState;
                // If address is already an object (parsed components), use it directly
                // Otherwise, parse it from a string
                let parsedAddress;
                if (address && typeof address === 'object' && !Array.isArray(address)) {
                    // Already parsed address components
                    parsedAddress = address;
                } else {
                    // Parse from string (or null/undefined)
                    parsedAddress = parseAddressString(address || '', type === "venue" ? name : null);
                }
                setInternalState({
                    address: parsedAddress,
                    location: location,
                    venue: venue,
                });
            }
        },
        getAddressString: () => formatAddressComponents(internalState.address),
        getLocation: () => internalState.location,
        recommend: () => recommendRef.current,
        // Validate and return validation result - also marks all fields as touched to show errors
        validate: () => {
            const { isValid, errors } = validateAddressComponents(internalState.address);
            // Mark all fields as touched so errors show immediately
            setTouched({
                venueName: true,
                streetAddress: true,
                city: true,
                state: true,
                zipCode: true
            });
            setValidationErrors(errors);
            return { isValid, errors };
        },
        // Get current validation state without triggering touch
        getValidationState: () => {
            const { isValid, errors } = validateAddressComponents(internalState.address);
            return { isValid, errors, validationErrors };
        },
        setErrors: (errors) => {
            // Accepts either a string or an object of field errors
            if (typeof errors === 'string') {
                setValidationErrors(prev => ({ ...prev, general: errors }));
                // Force all fields to touched so errors show immediately
                setTouched({
                    venueName: true,
                    streetAddress: true,
                    city: true,
                    state: true,
                    zipCode: true
                });
            } else if (typeof errors === 'object' && errors !== null) {
                setValidationErrors(prev => ({ ...prev, ...errors }));
                // Force all fields to touched so errors show immediately
                setTouched({
                    venueName: true,
                    streetAddress: true,
                    city: true,
                    state: true,
                    zipCode: true
                });
            }
        }
    }));

    // Handlers
    const clearAddress = () => {
        if (isControlled) {
            onChange?.({
                address: '',
                location: null,
                addressFields: null,
                venue: null
            });
        } else {
            setInternalState({
                address: parseAddressString(''),
                location: null,
                venue: null
            });
        }
        setLocationSuggestions([]);
    };

    const handleMapClick = async (latlng) => {
        const newLoc = {
            type: "Point",
            coordinates: [latlng.lng, latlng.lat]
        }
        
        // Immediately set the location so the marker appears
        setInternalState(prev => {
            const newState = {
                ...prev,
                location: newLoc,
                venue: null
            };

            isInternalUpdate.current = true;
            onChange?.({
                address: formatAddressComponents(prev.address),
                addressFields: prev.address,
                location: newLoc,
                venue: null
            });

            return newState;
        });

        // Reverse geocode to get address from coordinates
        try {
            setLocationLoading(true);
            const { data } = await axiosInstance.get(
                `${backendUrl}/influencer/geolocation/reverse?${new URLSearchParams({
                    latitude: latlng.lat,
                    longitude: latlng.lng,
                })}`
            );

            if (data?.address_components) {
                const googleAddress = parseGoogleAddress(data.address_components);
                const newState = {
                    address: {
                        venueName: '', // Keep venue name empty for map clicks
                        ...googleAddress
                    },
                    location: newLoc,
                    venue: null
                };

                setInternalState(newState);

                // Validate and show errors immediately after map click selection
                const { errors } = validateAddressComponents(newState.address);
                setValidationErrors(errors);
                setTouched({
                    venueName: true,
                    streetAddress: true,
                    city: true,
                    state: true,
                    zipCode: true
                });

                isInternalUpdate.current = true;
                onChange?.({
                    address: data.formatted_address,
                    addressFields: newState.address,
                    location: newLoc,
                    venue: null
                });
            }
        } catch (error) {
            console.error("Reverse geocode error:", error);
            // Location is already set, just couldn't get address - that's okay
        } finally {
            setLocationLoading(false);
        }
    };

    const handleFieldChange = (field, value) => {
        setInternalState(prev => {
            const newFields = { ...prev.address, [field]: value };
            const newState = {
                ...prev,
                address: newFields,
                // Clear venue when manually editing fields
                venue: prev.venue
            };

            // Validate the new fields
            const { errors } = validateAddressComponents(newFields);
            setValidationErrors(errors);

            isInternalUpdate.current = true;
            onChange?.({
                address: formatAddressComponents(newFields),
                addressFields: newFields,
                location: newState.location,
                venue: newState.venue
            });

            return newState;
        });
        setTouched(t => ({ ...t, [field]: true }));
    };

    const handleAddressChange = async (overrideValues = {}) => {
        setLocationLoading(true);
        // Allow passing new values to handle state timing issues
        const venueName = (overrideValues.venueName !== undefined ? overrideValues.venueName : internalState.address.venueName)?.trim() || '';
        const streetAddress = (overrideValues.streetAddress !== undefined ? overrideValues.streetAddress : internalState.address.streetAddress)?.trim() || '';
        
        // If both fields are filled, use only venueName for search (to avoid mixing venue name with street address)
        // Otherwise, use whichever field has content (normal behavior)
        let searchTerm = '';
        if (venueName && streetAddress) {
            // Both filled - use only venueName to search for venues by name
            searchTerm = venueName;
        } else {
            // Normal behavior - use whichever has content
            searchTerm = [venueName, streetAddress].filter(Boolean).join(' ').trim();
        }

        if (!searchTerm || searchTerm === "") {
            setLocationSuggestions([]);
            clearTimeout(locationSearchTimer.current);
            setLocationLoading(false);
            return;
        }

        clearTimeout(locationSearchTimer.current);
        locationSearchTimer.current = setTimeout(async () => {
            try {
                let autocompleteUrl = `${backendUrl}/influencer/geolocation/autocomplete?${new URLSearchParams({ input: searchTerm })}`;
                if (userLocation) {
                    console.log('userLocation found', userLocation)
                    // Use location bias with larger radius (50km) for better results
                    autocompleteUrl += `&latitude=${userLocation.lat}&longitude=${userLocation.lng}&radius=50000`
                }
                // Note: Removed cityCode=NYC restriction as it was too limiting
                // Google will still bias results based on user's IP location 
                const results = await axiosInstance.get(autocompleteUrl);
                if (results?.data) {
                    setLocationSuggestions(results.data);
                }
            } catch (error) {
                console.error("Autocomplete error:", error);
            } finally {
                setLocationLoading(false);
            }
        }, 700);
    };

    const selectLocationSuggestion = async (suggestion) => {
        if (!suggestion) return;

        try {
            setLocationLoading(true);

            if (suggestion?._id) {
                // Handle existing venue from database
                const addressFields = parseAddressString(suggestion.address, suggestion.name);
                const newState = {
                    address: {
                        venueName: suggestion.name,
                        ...addressFields
                    },
                    location: suggestion.location,
                    venue: suggestion
                };

                setInternalState(newState);
                setLocationSuggestions([]);

                // Validate and show errors immediately after selection
                const { errors } = validateAddressComponents(newState.address);
                setValidationErrors(errors);
                setTouched({
                    venueName: true,
                    streetAddress: true,
                    city: true,
                    state: true,
                    zipCode: true
                });

                if (isControlled) {
                    onChange?.({
                        address: formatAddressComponents(newState.address),
                        addressFields: newState.address,
                        location: newState.location,
                        venue: newState.venue
                    });
                }

                if (onSuggestionSelected) {
                    await onSuggestionSelected({
                        rawSuggestion: suggestion,
                        parsedAddress: newState.address,
                        coordinates: suggestion.location?.coordinates
                    });
                }

                if (suggestion.location?.coordinates) {
                    mapRef.current?.panTo({
                        lat: suggestion.location.coordinates[1],
                        lng: suggestion.location.coordinates[0]
                    });
                }
            } else {
                // Handle new location from Google Places
                const { data } = await axiosInstance.get(
                    `${backendUrl}/influencer/geolocation/place?${new URLSearchParams({
                        place_id: suggestion?.place_id,
                    })}`
                );

                if (!data) {
                    enqueueSnackbar('No place data received', { variant: 'error' });
                    return;
                }

                const googleAddress = parseGoogleAddress(data.address_components);
                const newState = {
                    address: {
                        venueName: data.name || '',
                        ...googleAddress
                    },
                    location: {
                        type: "Point",
                        coordinates: [data.geometry.location.lng, data.geometry.location.lat]
                    },
                    venue: null
                };

                setInternalState(newState);
                setLocationSuggestions([]);

                // Validate and show errors immediately after selection
                const { errors } = validateAddressComponents(newState.address);
                setValidationErrors(errors);
                setTouched({
                    venueName: true,
                    streetAddress: true,
                    city: true,
                    state: true,
                    zipCode: true
                });

                if (isControlled) {
                    onChange?.({
                        address: data.formatted_address,
                        addressFields: newState.address,
                        location: newState.location,
                        venue: null
                    });
                }

                mapRef.current?.panTo({
                    lat: data.geometry.location.lat,
                    lng: data.geometry.location.lng
                });
            }
        } catch (error) {
            console.error("Error selecting location:", error);
        } finally {
            setLocationLoading(false);
        }
    };

    // Render
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--inyc-space-10)" }}>
            {!hideLabel && (
                <div className={`flex justify-between items-center ${labelRowClassName}`}>
                    <div className="ui-label mb-kit-16">
                        location
                    </div>
                    {!addressEmpty && (
                        <div className="rounded-kit-full w-fit px-kit-12 py-kit-6 text-kit-xs bg-[--inyc-primary-light] cursor-pointer border border-[var(--inyc-input-border-color)]" onClick={clearAddress}>
                            clear
                        </div>
                    )}
                </div>
            )}

            {/* Venue Card - shown when a verified venue is selected */}
            {(internalState.venue && showVenueCard) && (
                <div className="scale-[0.9] w-full max-w-[450px] mx-auto flex justify-center items-center mb-kit-8">
                    <VenueCard
                        hideOwnershipStatus
                        className={'!mb-0 min-w-full'}
                        venue={internalState.venue}
                        onUnselect={clearAddress}
                    />
                </div>
            )}

            {/* Address Fields Section */}
            <div className="" style={{ display: "flex", flexDirection: "column", gap: "var(--inyc-space-10)" }}>
                <div className="px-kit-16">
                    {/* Search Suggestions */}
                    {locationLoading ? (
                        <div className="w-full flex justify-center items-center p-kit-16">
                            <Loader />
                        </div>
                    ) : (
                        locationSuggestions.length > 0 && (
                            <div className="mt-kit-8 w-full overflow-x-scroll p-kit-16 flex justify-start items-stretch gap-kit-16 bg-[var(--inyc-primary)]">
                                {locationSuggestions
                                    .filter(suggestion => suggestionFilter ? suggestionFilter(suggestion) : true)
                                    .map((suggestion, i) => renderSuggestion ? (
                                        <div
                                            className="relative p-kit-16 whitespace-nowrap max-w-[250px] text-kit-xs rounded-[var(--inyc-input-border-radius)] cursor-pointer border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary-light)]"
                                            key={i} onClick={() => selectLocationSuggestion(suggestion)}>
                                            {renderSuggestion(suggestion, i)}
                                        </div>
                                    ) : (
                                        <div
                                            key={i}
                                            onClick={() => selectLocationSuggestion(suggestion)}
                                            className="relative p-kit-16 whitespace-nowrap max-w-[250px] text-kit-xs rounded-[var(--inyc-input-border-radius)] cursor-pointer border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary-light)]"
                                        >
                                            {suggestion._id ? (
                                                <div className="px-kit-8 py-kit-2 flex justify-center items-center gap-kit-4 absolute top-[-1rem] right-[-0.5rem] bg-black text-white rounded-kit-sm">
                                                    <img src={Logo} alt="inyc verified" className="w-[1.5rem] h-[1.5rem] object-contain invert-[1]" />
                                                    <div className="leading-[1rem] font-['Primary-Regular'] text-kit-2xs">verified</div>
                                                </div>
                                            ) : (
                                                <div className="px-kit-8 py-kit-2 flex justify-center items-center gap-kit-4 absolute top-[-1rem] right-[-0.5rem] bg-[var(--inyc-primary-light)] text-[var(--inyc-primary-text-color)] rounded-kit-sm border border-[var(--inyc-input-border-color)] shadow-sm">
                                                    <FcGoogle size={16} />
                                                    <div className="leading-[1rem] font-['Primary-Regular'] text-kit-2xs">maps</div>
                                                </div>
                                            )}
                                            <div>
                                                {suggestion._id ? suggestion.name : suggestion.structured_formatting?.main_text || suggestion.description}
                                                {(suggestion.address || suggestion.structured_formatting?.secondary_text || suggestion.description) && (
                                                    <div className="text-gray-500 max-w-full overflow-hidden overflow-ellipsis">
                                                        {suggestion._id ? suggestion.address : (suggestion.structured_formatting?.secondary_text || suggestion.description)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )
                                    )}
                            </div>
                        )
                    )}

                    {/* Address Input Fields */}
                    <Input
                        value={internalState.address.venueName}
                        onChange={(e) => {
                            const newValue = e.target.value;
                            handleFieldChange('venueName', newValue);
                            handleAddressChange({ venueName: newValue });
                            if (newValue) setHasInteracted(true);
                        }}
                        onBlur={() => setTouched(t => ({ ...t, venueName: true }))}
                        placeHolder="business / venue name"
                        error={touched.venueName ? validationErrors.venueName : ''}
                    />
                    <Input
                        value={internalState.address.streetAddress}
                        onChange={(e) => {
                            const newValue = e.target.value;
                            handleFieldChange('streetAddress', newValue);
                            handleAddressChange({ streetAddress: newValue });
                            if (newValue) setHasInteracted(true);
                        }}
                        onBlur={() => setTouched(t => ({ ...t, streetAddress: true }))}
                        placeHolder="street address"
                        error={touched.streetAddress ? validationErrors.streetAddress : ''}
                    />

                    <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--inyc-space-16)" }}>
                        <div className="flex-1">
                            <Input
                                value={internalState.address.city}
                                onChange={(e) => handleFieldChange('city', e.target.value)}
                                onBlur={() => setTouched(t => ({ ...t, city: true }))}
                                placeHolder="city"
                                error={touched.city ? validationErrors.city : ''}
                            />
                        </div>
                        <div className="flex-1">
                            <Input
                                value={internalState.address.state}
                                onChange={(e) => handleFieldChange('state', e.target.value)}
                                onBlur={() => setTouched(t => ({ ...t, state: true }))}
                                placeHolder="state"
                                error={touched.state ? validationErrors.state : ''}
                            />
                        </div>
                    </div>

                    <Input
                        value={internalState.address.zipCode}
                        onChange={(e) => handleFieldChange('zipCode', e.target.value)}
                        onBlur={() => setTouched(t => ({ ...t, zipCode: true }))}
                        placeHolder="zip code"
                        error={touched.zipCode ? validationErrors.zipCode : ''}
                    />
                </div>
            </div>

            {/* Helper text / warning */}
            {!internalState?.location?.coordinates && hasInteracted && (
                <div className="text-kit-sm px-kit-16 text-warning">
                    ⚠ search and select a google result
                    <br />
                    or manually input address then confirm location by dropping a pin on the map
                </div>
            )}

            {/* Integrated Map Section */}
            <div className="px-kit-16 mt-kit-8">
                {/* Guide text for map */}
                <div className="text-kit-md font-['Primary-Regular'] text-[var(--inyc-primary-text-color)] mb-kit-16">
                    you can also select the exact location on the map, and edit the address above as necessary
                </div>
                <div className="w-full mx-auto rounded-[var(--inyc-input-border-radius)] overflow-hidden h-[180px] md:h-[220px]">
                    <DashboardMap
                        className="w-full h-full shadow-sm"
                        Ref={mapRef}
                        userLocation={userLocation}
                        style={{ borderRadius: "var(--inyc-input-border-radius)" }}
                        onSearchResultSelected={() => { }}
                        onMapClick={handleMapClick}
                        markers={[
                            internalState?.location?.coordinates ? {
                                zIndexOffset: 1000,
                                icon: eventMarker,
                                lat: internalState.location?.coordinates[1],
                                lng: internalState.location?.coordinates[0],
                                props: {
                                    Popup: (props) => {
                                        const { onClose } = props;
                                        return <Popup
                                            style={{
                                                backgroundColor: "var(--inyc-primary-light)",
                                            }}
                                            className="bg-[--inyc-primary] w-full"
                                            {...props}
                                        >
                                            <div className="bg-[--inyc-primary] w-full" style={{ padding: 'var(--inyc-space-8)', minWidth: '15rem', borderRadius: 'var(--inyc-radius-xs)' }}>
                                                {internalState.venue && <>
                                                    <div className="text-kit-2xs text-[--inyc-primary-text-color-light] font-['Primary-Medium']">
                                                        venue
                                                    </div>
                                                    <div className="text-kit-base mb-kit-4 font-['Primary-Medium'] text-[--inyc-primary-text-color]">
                                                        {internalState.venue?.name}
                                                    </div>
                                                </>}
                                                <div className="text-kit-2xs mb-kit-8 font-['Primary-Regular'] text-[--inyc-primary-text-color-light]">
                                                    {formatAddressComponents(internalState?.address) || <div className="text-kit-xs ml-kit-8 font-['Primary-Regular'] text-[--inyc-primary-text-color-light]">no address</div>}
                                                </div>
                                                <MarkerPopupButton
                                                    Icon={MdClear}
                                                    text="clear"
                                                    iconsize={13}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        clearAddress();
                                                        onClose();
                                                    }}
                                                />
                                            </div>
                                        </Popup>
                                    }
                                }
                            } : null,
                            ...mapMarkers
                        ].filter(Boolean)}
                    />
                </div>
            </div>

            {/* Recommend Checkbox - at the very bottom */}
            {/* {recommendCheckbox && !internalState.venue?._id && (
                <div className="px-kit-16 mt-kit-8">
                    <CheckBox
                        label={
                            <div className="text-kit-base">
                                recommend this venue to {" "}
                                <span className="font-['Primary-Bold']">in—yc</span>
                            </div>
                        }
                        Ref={recommendRef}
                        className="scale-[0.85]"
                    />
                </div>
            )} */}
        </div>
    );
});

export default AddressHandler;